import { isLocalOidcRoundtripAllowed } from "@/lib/authMode";
import { useAuthFlow } from "@/hooks/useAuthFlow";
import {
  Mail,
  Lock,
  ArrowLeft,
  Zap,
  Loader2,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  ShieldCheck,
  Globe,
} from "lucide-react";
import { LandingNavbar } from "@/components/landing/LandingNavbar";
import { AuthBrandMark } from "@/components/auth/AuthBrandMark";
import { AuthHeroPanel } from "@/components/auth/AuthHeroPanel";
import { MicrosoftLogo } from "@/components/auth/MicrosoftLogo";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { domainMatchesProvider, extractEmailDomain } from "@/lib/authSso";
import { DEV_USERS } from "@/config/devUsers";

export default function Auth() {
  const {
    t,
    isLoading,
    errors,
    showForgotPassword,
    setShowForgotPassword,
    resetEmail,
    setResetEmail,
    resetEmailSent,
    quickLoggingIn,
    autoSeedStatus,
    isDevMode,
    isCheckingDevMode,
    passwordAuthAllowed,
    ssoLoading,
    ssoProviders,
    ssoProvidersLoading,
    ssoInProgress,
    checkingSession,
    ssoEmail,
    setSsoEmail,
    ssoEmailError,
    setSsoEmailError,
    showSsoEmailInput,
    setShowSsoEmailInput,
    ssoDomain,
    setSsoDomain,
    loginEmail,
    setLoginEmail,
    loginPassword,
    setLoginPassword,
    handleLogin,
    handleForgotPassword,
    handleBackToLogin,
    handleQuickLogin,
    handleSsoEmailLogin,
    performSsoLogin,
    handleCheckSession,
    handleCancelSso,
  } = useAuthFlow();
  const { enabled: landingEnabled } = useFeatureFlag(
    "landing-page",
    FEATURE_FLAGS["landing-page"] ?? false,
  );
  const { enabled: quickLoginEnabled } = useFeatureFlag(
    "quick-login",
    FEATURE_FLAGS["quick-login"] ?? true,
  );

  // Forgot Password View
  if (showForgotPassword && passwordAuthAllowed) {
    return (
      <div
        className="min-h-[100dvh] flex items-center justify-center bg-surface p-4"
        data-testid="auth-shell"
      >
        {landingEnabled && <LandingNavbar />}
        <div className={`w-full max-w-md animate-fade-in ${landingEnabled ? "pt-16" : ""}`}>
          <AuthBrandMark appName={t("auth.appName")} className="mb-8" />

          <Card
            className="border-outline-variant bg-surface-container-lowest shadow-none"
            data-testid="auth-card"
          >
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl">{t("auth.forgotPasswordTitle")}</CardTitle>
              <CardDescription>
                {resetEmailSent ? t("auth.resetEmailSent") : t("auth.forgotPasswordDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resetEmailSent ? (
                <div className="space-y-4">
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4 text-center">
                    <Mail className="h-12 w-12 text-success mx-auto mb-3" />
                    <p className="text-sm text-foreground">
                      {t("auth.emailSentToPrefix")} <strong>{resetEmail}</strong>{" "}
                      {t("auth.emailSentToSuffix")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">{t("auth.checkSpam")}</p>
                  </div>
                  <Button variant="outline" className="w-full" onClick={handleBackToLogin}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t("auth.backToLogin")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">{t("auth.email")}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder={t("auth.emailPlaceholder")}
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="pl-10"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.reset_email && (
                      <p className="text-sm text-destructive">{errors.reset_email}</p>
                    )}
                  </div>

                  <Button type="submit" variant="action" className="w-full" disabled={isLoading}>
                    {isLoading ? t("auth.sending") : t("auth.sendResetLink")}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={handleBackToLogin}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t("auth.backToLogin")}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">{t("auth.appTagline")}</p>
        </div>
      </div>
    );
  }

  return (
    <main className="flex min-h-[100dvh] bg-surface-container-lowest" data-testid="auth-shell">
      {landingEnabled && <LandingNavbar />}

      {/* LINKS: Deep-Blue Industrial-Hero-Panel (ab lg sichtbar; Mobile stapelt) */}
      <AuthHeroPanel />

      {/* RECHTS: Sign-in-Bereich.
          TES-1087: oben verankert (items-start) statt vertikal zentriert — async
          nachladende Blöcke (SSO-Provider, Quick-Login) ändern die Spaltenhöhe und
          würden die Anmeldebox bei jeder Höhenänderung re-zentrieren (Layout-Shift). */}
      <section
        className={`flex w-full items-start justify-center bg-surface-container-lowest p-6 sm:p-8 lg:w-2/5 lg:p-16 xl:px-24 xl:py-20 ${landingEnabled ? "pt-20 lg:pt-24" : ""}`}
      >
        <div className="w-full max-w-[420px] animate-fade-in">
          {/* Header: Logo (Safety-Yellow-Tile + Wortmarke) */}
          <div className="mb-10">
            <AuthBrandMark appName={t("auth.appName")} className="mb-8 justify-start gap-2" />
            <h2 className="font-display text-h3 text-foreground">
              {t("auth.splitScreen.signInTitle")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("auth.splitScreen.signInDesc")}</p>
          </div>

          <Card className="border-none bg-transparent shadow-none" data-testid="auth-card">
            <CardContent className="p-0">
              {/* TES-1085/TES-495: Kein Registrieren-Tab mehr; lokale Passwort-Auth
                  rendert nur außerhalb Production Mode. Produktion nutzt Entra/SSO. */}
              {passwordAuthAllowed && (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-label-caps text-muted-foreground">
                      {t("auth.email")}
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder={t("auth.emailPlaceholder")}
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="login-password"
                        className="text-label-caps text-muted-foreground"
                      >
                        {t("auth.password")}
                      </Label>
                      <Button
                        variant="link"
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setResetEmail(loginEmail);
                        }}
                        className="h-auto p-0 text-xs font-bold text-primary"
                      >
                        {t("auth.forgotPassword")}
                      </Button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.password && (
                      <p className="text-sm text-destructive">{errors.password}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="action"
                    className="group w-full gap-2 py-6 text-label-caps"
                    disabled={isLoading || ssoLoading}
                  >
                    {isLoading ? (
                      t("auth.loggingIn")
                    ) : (
                      <>
                        {t("auth.login")}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Local-E2E SSO/OIDC-Roundtrip-Button (TES-707 WP5) — rendert NUR gegen
                  lokalen Stack (run-e2e-local -WithSso). supabase.auth.signInWithSSO()
                  ist SAML-only; der lokale GoTrue kennt keine generischen OIDC-Provider.
                  Der Button fährt den OAuth2 Authorization-Code-Flow deshalb direkt
                  gegen den Mock-IdP (ghcr.io/navikt/mock-oauth2-server, Port 8888). */}
              {isLocalOidcRoundtripAllowed() && (
                <>
                  {passwordAuthAllowed && (
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-surface-container-lowest px-2 text-muted-foreground">
                          {t("auth.or")}
                        </span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={isLoading}
                    onClick={() => {
                      const authorizeUrl = new URL("http://127.0.0.1:8888/default/authorize");
                      authorizeUrl.searchParams.set("client_id", "checkmaty-local-e2e");
                      authorizeUrl.searchParams.set(
                        "redirect_uri",
                        `${window.location.origin}/auth/callback`,
                      );
                      authorizeUrl.searchParams.set("response_type", "code");
                      authorizeUrl.searchParams.set("scope", "openid profile email");
                      authorizeUrl.searchParams.set("state", crypto.randomUUID());
                      authorizeUrl.searchParams.set("nonce", crypto.randomUUID());
                      window.location.assign(authorizeUrl.toString());
                    }}
                    data-testid="sso-login-button"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t("auth.signInWithSsoLocalE2e")}
                  </Button>
                </>
              )}

              {/* Microsoft SSO Section — nur wenn Provider vorhanden oder in Progress.
                  TES-1087: Während des Ladens wird bewusst NICHTS gerendert (kein Skeleton):
                  auf Instanzen ohne konfigurierte Provider würde ein Skeleton erscheinen und
                  wieder verschwinden → Layout-Shift. „Lädt" und „leer" sind hier identisch
                  unsichtbar; nur ein nicht-leeres Ergebnis blendet den Block ein. */}
              {(ssoInProgress || (!ssoProvidersLoading && ssoProviders.length > 0)) && (
                <>
                  {passwordAuthAllowed && (
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-surface-container-lowest px-2 text-muted-foreground">
                          {t("auth.or")}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Smart Microsoft SSO Login */}
                  {ssoInProgress ? (
                    /* SSO In Progress - Show status and "I'm back" button */
                    <div className="space-y-4">
                      <div className="rounded-lg border border-outline-variant bg-surface-container-low p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="relative">
                            <MicrosoftLogo className="h-8 w-8" />
                            <div className="absolute -top-1 -right-1">
                              <ExternalLink className="h-3 w-3 text-primary" />
                            </div>
                          </div>
                          <div>
                            <h3 className="font-medium text-foreground">
                              {t("auth.oauth.ssoInProgressTitle")}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {t("auth.oauth.ssoInProgressDesc")}
                            </p>
                          </div>
                        </div>

                        <Button
                          onClick={handleCheckSession}
                          disabled={checkingSession}
                          className="w-full"
                          variant="default"
                        >
                          {checkingSession ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {t("auth.oauth.checkingSession")}
                            </>
                          ) : (
                            <>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              {t("auth.oauth.imBackButton")}
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-muted-foreground text-center mt-2">
                          {t("auth.oauth.imBackDesc")}
                        </p>
                      </div>

                      {/* Troubleshooting hints */}
                      <div className="rounded-lg border border-outline-variant bg-surface-container-low p-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <AlertCircle className="h-4 w-4 text-muted-foreground" />
                          {t("auth.oauth.troubleshootingTitle")}
                        </div>
                        <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                          <li>{t("auth.oauth.troubleshootingHint1")}</li>
                          <li>{t("auth.oauth.troubleshootingHint2")}</li>
                          <li>{t("auth.oauth.troubleshootingHint3")}</li>
                          <li>{t("auth.oauth.troubleshootingHint4")}</li>
                        </ul>
                      </div>

                      <Button onClick={handleCancelSso} variant="ghost" className="w-full">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t("auth.oauth.cancelSso")}
                      </Button>
                    </div>
                  ) : ssoProviders.length > 0 ? (
                    /* SSO Provider available - Email-based domain matching */
                    !showSsoEmailInput && ssoDomain ? (
                      /* Saved domain - direct login */
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <MicrosoftLogo className="h-4 w-4" />
                          {t("auth.ssoSavedLoginAs", { domain: ssoDomain })}
                        </div>
                        <Button
                          onClick={() => {
                            const provider = ssoProviders.find((p) =>
                              p.sso_domains?.some((d) => d.toLowerCase() === ssoDomain),
                            );
                            if (provider?.supabase_provider_id) {
                              performSsoLogin(provider.supabase_provider_id);
                            }
                          }}
                          disabled={ssoLoading || isLoading}
                          variant="outline"
                          className="w-full"
                        >
                          {ssoLoading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <MicrosoftLogo className="mr-2 h-4 w-4" />
                          )}
                          {t("auth.signInWithMicrosoft")}
                        </Button>
                        <Button
                          variant="link"
                          type="button"
                          onClick={() => {
                            localStorage.removeItem("lastSsoDomain");
                            setSsoDomain("");
                            setShowSsoEmailInput(true);
                          }}
                          className="w-full h-auto p-0 text-sm"
                        >
                          {t("auth.ssoChangeDomain")}
                        </Button>
                      </div>
                    ) : (
                      /* Email input form with domain recognition */
                      <form onSubmit={handleSsoEmailLogin} className="space-y-3">
                        <div className="space-y-2">
                          <Label
                            htmlFor="sso-email"
                            className="text-sm font-medium flex items-center gap-2"
                          >
                            <MicrosoftLogo className="h-4 w-4" />
                            {t("auth.ssoLogin")}
                          </Label>
                          <div className="relative">
                            <Input
                              id="sso-email"
                              type="email"
                              placeholder={t("auth.companyEmail")}
                              value={ssoEmail}
                              onChange={(e) => {
                                setSsoEmail(e.target.value);
                                setSsoEmailError("");
                              }}
                              disabled={ssoLoading || isLoading}
                              className="pr-10"
                            />
                            {domainMatchesProvider(extractEmailDomain(ssoEmail), ssoProviders) ? (
                              <CheckCircle
                                className="absolute right-3 top-2.5 h-5 w-5 text-green-500"
                                aria-label={t("auth.ssoDomainRecognized")}
                              />
                            ) : null}
                          </div>
                          {ssoEmailError && (
                            <p className="text-xs text-destructive">{ssoEmailError}</p>
                          )}
                          <p className="text-xs text-muted-foreground">{t("auth.ssoLoginHint")}</p>
                        </div>
                        <Button
                          type="submit"
                          disabled={ssoLoading || isLoading || !ssoEmail.trim()}
                          variant="outline"
                          className="w-full"
                        >
                          {ssoLoading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="mr-2 h-4 w-4" />
                          )}
                          {ssoEmail.includes("@") &&
                          domainMatchesProvider(extractEmailDomain(ssoEmail), ssoProviders)
                            ? t("auth.ssoLoginAs", { domain: ssoEmail.trim().split("@")[1] })
                            : t("auth.signInWithMicrosoft")}
                        </Button>
                      </form>
                    )
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>

          {/* Onboarding-Hinweis (TES-1085): einziger Hinweis für neue Nutzer — Zugang nur
            per administrativer Freigabe, kein Selbstregistrierungs-Flow mehr.
            Linker Safety-Yellow-Akzent (border-l-4 border-secondary). */}
          <div className="mt-8 rounded-lg border-l-4 border-secondary bg-surface-container p-4">
            <p className="text-label-caps text-foreground">{t("auth.splitScreen.newToPlatform")}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t("auth.splitScreen.newToPlatformDesc")}
            </p>
          </div>

          {/* Quick Login Section - Loading Skeleton.
            TES-1087: Skeleton reserviert exakt die ENDHÖHE der echten Karte
            (Header-Zeile = CardTitle text-sm → h-5; Buttons = py-2 + 2 Zeilen
            text-xs → h-12), damit der Wechsel Skeleton → echte Karte die
            Spaltenhöhe nicht verändert (kein Layout-Shift). */}
          {isCheckingDevMode && (
            <Card
              className="mt-4 border-outline-variant bg-surface-container-lowest shadow-none"
              data-testid="quick-login-card"
            >
              <CardHeader className="py-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-5 w-32" />
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {DEV_USERS.map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full rounded-md" />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Login Section - Only when not in production mode.
            TES-1087: Das Button-Grid rendert IMMER (höhenstabil); während des
            Bootstrap-Checks sind die Buttons nur disabled + Spinner im Header,
            statt das Grid durch eine niedrigere Loader-Zeile zu ersetzen. */}
          {!isCheckingDevMode && isDevMode && quickLoginEnabled && DEV_USERS.length > 0 && (
            <Card
              className="mt-4 border-outline-variant bg-surface-container-lowest shadow-none"
              data-testid="quick-login-card"
            >
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-warning" />
                    <CardTitle className="text-sm">{t("auth.quickLogin")}</CardTitle>
                  </div>
                  {autoSeedStatus === "checking" && (
                    <Loader2
                      className="h-4 w-4 animate-spin text-muted-foreground"
                      aria-label={t("auth.checkingUsers")}
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {DEV_USERS.map((devUser) => (
                    <Button
                      key={devUser.email}
                      variant="ghost"
                      size="sm"
                      className="justify-start text-left h-auto py-2"
                      onClick={() => handleQuickLogin(devUser.email)}
                      disabled={quickLoggingIn !== null || autoSeedStatus === "checking"}
                    >
                      {quickLoggingIn === devUser.email ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-2 shrink-0" />
                      ) : (
                        <Zap className="h-3 w-3 text-warning mr-2 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-xs truncate">{devUser.label}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {devUser.email}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer: dekorativer System-Status + Security/Sprache-Icons */}
          <div className="mt-10 flex items-center justify-between border-t border-outline-variant pt-6">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-success" aria-hidden="true" />
              <span className="text-data-tabular uppercase text-muted-foreground">
                {t("auth.splitScreen.systemStatusOnline")}
              </span>
            </div>
            <div className="flex items-center gap-4 text-outline-variant">
              <ShieldCheck
                className="h-[18px] w-[18px]"
                aria-label={t("auth.splitScreen.securityInfo")}
              />
              <Globe
                className="h-[18px] w-[18px]"
                aria-label={t("auth.splitScreen.languageInfo")}
              />
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">{t("auth.appTagline")}</p>
        </div>
      </section>
    </main>
  );
}
